import { useState } from 'react';
import { theme } from '../theme.js';
import { useCalendarFeed, useResetCalendarFeed } from '../lib/queries.js';
import { emitError } from '../lib/toastBus.js';
import { Icon } from './Icon.js';

/**
 * "Add shifts to your calendar": a private feed address for Google / Apple /
 * Outlook. With `userId` (a leader or admin, for a member without the app)
 * it shows that member's link to pass on.
 */
export function CalendarCard({ orgId, userId, name }: { orgId?: string; userId?: string; name?: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data, isFetching } = useCalendarFeed(open, orgId, userId);
  const reset = useResetCalendarFeed();
  const mine = !userId;
  const https = data?.url ?? '';
  const webcal = https.replace(/^https:/, 'webcal:');

  async function copy() {
    try {
      await navigator.clipboard.writeText(https);
      setCopied(true);
    } catch {
      emitError('Couldn’t copy — press and hold the link to copy it');
    }
  }

  return (
    <div style={{
      margin: mine ? '12px 16px' : '0 16px 8px', padding: '12px 14px', borderRadius: 12,
      background: theme.surface, border: `1px solid ${theme.border}`, fontFamily: theme.font,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Icon name="calendar" size={18} style={{ color: theme.textMuted, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
            {mine ? 'Add shifts to your calendar' : `${name ?? 'Their'} shifts in a calendar`}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>
            {mine ? 'Shifts, covers and activities you’re going to, kept up to date in Google, Apple or Outlook.'
              : 'A link they can add to their own calendar. Send it to them however suits.'}
          </div>
        </div>
        {!open && <button onClick={() => setOpen(true)} style={small}>{mine ? 'Set up' : 'Get link'}</button>}
      </div>

      {open && (isFetching && !data ? <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 10 }}>Getting your link…</div> : data && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mine && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              <a href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`} target="_blank" rel="noreferrer" style={choice}>Google</a>
              <a href={webcal} style={choice}>Apple / iPhone</a>
              <a href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(https)}&name=${encodeURIComponent('MusterTap shifts')}`} target="_blank" rel="noreferrer" style={choice}>Outlook</a>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <input readOnly value={https} onFocus={(e) => e.target.select()} aria-label="Calendar link"
              style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 12, borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.textDim, fontFamily: theme.font }} />
            <button onClick={() => void copy()} style={small}>{copied ? 'Copied' : 'Copy link'}</button>
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.45 }}>
            Calendar apps check for changes every few hours (Google can take up to a day), so for last-minute changes look in MusterTap.
            {' '}Anyone with this link can see {mine ? 'your' : 'their'} shifts — keep it private.
          </div>
          {mine && (
            <button onClick={() => {
              if (window.confirm('Make a new link? The old one stops working, so you’ll need to add the calendar again.')) {
                reset.mutate(undefined, { onSuccess: () => setCopied(false) });
              }
            }} disabled={reset.isPending} style={{ ...link, alignSelf: 'flex-start' }}>
              {reset.isPending ? 'Making a new link…' : 'Shared it by mistake? Make a new link'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

const small: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface2,
  color: theme.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font, flexShrink: 0,
};
const choice: React.CSSProperties = {
  padding: '9px 4px', borderRadius: 8, background: theme.accent, color: theme.accentInk, textAlign: 'center',
  fontSize: 13, fontWeight: 700, textDecoration: 'none', fontFamily: theme.font,
};
const link: React.CSSProperties = {
  padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: theme.textDim,
  fontSize: 12, fontWeight: 600, textDecoration: 'underline', fontFamily: theme.font,
};
